/**
 * The editor's state.
 *
 * One store holds four separate things, and it matters which is which:
 *
 *   doc        The document. Undoable. Every change goes through commit().
 *   selection  What is selected. Not undoable - undoing a move should not
 *              also change what is selected, or the next undo lands somewhere
 *              the customer is not looking.
 *   view       Zoom and pan. Not undoable, for the same reason.
 *   history    Past and future documents.
 *
 * History stores whole documents rather than diffs. That is affordable because
 * every change here is immutable: an edit to one layer rebuilds that layer and
 * that page, and every other page in the snapshot is the same object as before.
 * Deep-cloning anywhere in this file would quietly turn a cheap history into an
 * expensive one.
 */

import { useMemo } from 'react';
import { create } from 'zustand';
import type { Colour, Design, ImageLayer, Layer, Page, TrimboxDocument } from '../model/types';
import type { Axis, Guide } from '../canvas/snapping';
import { findLayer } from '../model/types';
import { fromActive } from '../canvas/stack';
import { isUnit, type Unit } from '../model/units';

/** Where the customer's choice of unit is remembered, in their own browser. */
const UNIT_KEY = 'trimbox.unit';

function rememberedUnit(): Unit {
  try {
    const stored = window.localStorage.getItem(UNIT_KEY);

    return isUnit(stored) ? stored : 'mm';
  } catch {
    // Storage refused - private mode, or a locked-down browser. Millimetres,
    // which is what the shop and the press both speak.
    return 'mm';
  }
}

/** A guard, not a policy. Real documents never come close. */
const HISTORY_LIMIT = 500;

/** Consecutive commits with the same key merge if they land within this. */
const COALESCE_MS = 600;

export interface View {
  zoom: number;
  /** Where the page's top-left sits in the viewport, in screen pixels. */
  x: number;
  y: number;
  /** Guides, marks and handles hidden, so the customer sees the card. */
  preview: boolean;
  showGrid: boolean;
  showRulers: boolean;
  /** The customer's own ruler guides. Hidden in preview like everything else. */
  showGuides: boolean;
}

interface HistoryEntry {
  doc: TrimboxDocument;
  label: string;
  key: string | null;
  at: number;
}

export interface CommitOptions {
  /** Merges with the previous commit when it shares this key and is recent. */
  coalesce?: string;
}

interface EditorState {
  design: Design | null;
  doc: TrimboxDocument | null;
  pageIndex: number;
  selection: string[];
  view: View;
  past: HistoryEntry[];
  future: HistoryEntry[];
  /** Set on every commit, cleared when a save lands. */
  dirty: boolean;
  /** Snap guides currently showing. Only ever non-empty mid-drag. */
  guides: Guide[];
  /**
   * A ruler guide being dragged off a ruler and not yet placed.
   *
   * Held here rather than in the ruler component because the line is drawn on
   * the canvas while the pointer is still over the ruler.
   */
  drafting: { axis: Axis; at: number } | null;
  /** The text layer being typed into, if any. */
  editing: string | null;
  /**
   * The caret, as character offsets into that layer's text.
   *
   * Kept here rather than in the component because two things far apart need
   * it: the invisible textarea that owns the keyboard, and the Konva shapes
   * that draw the caret and the highlight.
   */
  caret: { start: number; end: number };
  /**
   * The picture being cropped, if any.
   *
   * Alongside `editing` rather than folded into it: both take over the canvas
   * and the keyboard, but they answer to different keys and only one of them
   * can be true at a time, which is easier to keep right when they are separate
   * fields than when one field means three things.
   */
  cropping: string | null;
  /**
   * What the crop was when the gesture began, so Escape can put it back.
   *
   * Undo would also put it back, but a customer who has spent a minute nudging
   * a photograph does not think of that minute as a list of steps.
   */
  cropWas: ImageLayer['crop'] | null;
  /**
   * The drawing whose points are being edited, if any.
   *
   * A third mode beside typing and cropping, and like them it takes the canvas
   * and the keyboard: Delete removes a point rather than the drawing, and the
   * resize handles give way to the path's own.
   */
  shaping: string | null;
  /**
   * Where a colour picked from the design goes, while the eyedropper is out.
   *
   * A function rather than a field name, because the colour can be going to a
   * text's fill, a shape's outline or the third stop of a gradient, and only
   * the control that asked knows which.
   */
  sampling: ((colour: Colour) => void) | null;
  /**
   * The unit lengths are shown and typed in.
   *
   * Beside the view rather than in it, because opening a design resets the
   * view - and a customer who chose inches did not choose them for one card.
   */
  unit: Unit;

  open: (design: Design) => void;
  commit: (label: string, next: (doc: TrimboxDocument) => TrimboxDocument, options?: CommitOptions) => void;
  undo: () => void;
  redo: () => void;
  markSaved: () => void;

  page: () => Page | null;
  updatePage: (label: string, next: (page: Page) => Page, options?: CommitOptions) => void;
  updateLayer: (id: string, label: string, next: (layer: Layer) => Layer, options?: CommitOptions) => void;

  select: (ids: string[]) => void;
  toggleSelect: (id: string) => void;
  selected: () => Layer[];

  goToPage: (index: number) => void;
  setView: (view: Partial<View>) => void;
  setGuides: (guides: Guide[]) => void;
  /** The guide being dragged off a ruler, or null when nothing is being placed. */
  setDrafting: (drafting: { axis: Axis; at: number } | null) => void;
  /** Adds a ruler guide to the page being edited. */
  addGuide: (axis: Axis, at: number) => void;
  /** Moves one, by its place in the page's list for that axis. */
  moveGuide: (axis: Axis, index: number, at: number) => void;
  removeGuide: (axis: Axis, index: number) => void;
  clearGuides: () => void;

  beginEditing: (id: string, caret?: { start: number; end: number }) => void;
  endEditing: () => void;
  setCaret: (caret: { start: number; end: number }) => void;

  beginCrop: (id: string, was: ImageLayer['crop']) => void;
  /** @param keep false puts the crop back to what it was when this began. */
  endCrop: (keep?: boolean) => void;

  beginShaping: (id: string) => void;
  endShaping: () => void;

  /**
   * Asks the app to open a side panel.
   *
   * The canvas knows a customer just double-clicked a QR code; it does not know
   * what a panel is, and should not. It says what it wants and the app answers,
   * the same way cropping and point editing already work.
   */
  wantPanel: string | null;
  askPanel: (which: string) => void;
  panelAsked: () => void;

  /**
   * The layer whose name is being typed, in the layers list.
   *
   * Here rather than in the panel because renaming is asked for from the
   * canvas's own menu, and the panel may not even be open when it is: the ask
   * opens the list and puts the caret in the right row.
   */
  renaming: string | null;
  beginRenaming: (id: string) => void;
  endRenaming: () => void;

  beginSampling: (to: (colour: Colour) => void) => void;
  endSampling: () => void;

  setUnit: (unit: Unit) => void;
}

const startingView: View = {
  zoom: 1,
  x: 0,
  y: 0,
  preview: false,
  showGrid: false,
  showRulers: true,
  showGuides: true,
};

/** Replaces one page, leaving every other page the same object. */
/** A page with one axis of its ruler guides rewritten. */
function withGuides(page: Page, axis: Axis, next: (list: number[]) => number[]): Page {
  const guides = { x: page.guides?.x ?? [], y: page.guides?.y ?? [] };

  return { ...page, guides: { ...guides, [axis]: next(guides[axis]) } };
}

/** In order, rounded as the document stores lengths, and never twice over. */
function tidy(list: number[]): number[] {
  const seen = new Set<number>();

  for (const at of list) {
    if (Number.isFinite(at)) seen.add(Math.round(at * 100) / 100);
  }

  return [...seen].sort((a, b) => a - b).slice(0, 40);
}

function withPage(doc: TrimboxDocument, index: number, next: (page: Page) => Page): TrimboxDocument {
  const page = doc.pages[index];

  if (!page) return doc;

  const pages = doc.pages.slice();
  pages[index] = next(page);

  return { ...doc, pages };
}

/**
 * Whether the drawing being edited is still one, after an undo or a redo.
 *
 * Undoing "Edit points" on a star puts the star back, and a star has no points
 * to edit - but the mode would stay on, hiding the resize handles, until the
 * customer thought to click somewhere else.
 */
function stillShaping(doc: TrimboxDocument, pageIndex: number, shaping: string | null): string | null {
  if (!shaping) return null;

  const page = doc.pages[pageIndex];
  const layer = page ? findLayer(page.layers, shaping) : null;

  return layer && layer.type === 'shape' && layer.shape === 'free' ? shaping : null;
}

/**
 * A group's children, carried with the box that holds them.
 *
 * Children are stored in the page's own millimetres, not relative to the group,
 * so moving or resizing a group has to move and resize them too. Without this
 * the box changed and everything inside it stayed exactly where it was: the
 * group appeared not to move at all, and could not be resized.
 *
 * Text is scaled by its type size as well as its box, because a heading whose
 * frame doubles and whose letters do not has not been resized - it has been
 * given room.
 */
export function carry(before: Layer, after: Layer): Layer {
  if (before.type === 'image' && after.type === 'image') return carryMask(before, after);
  if (before.type !== 'group' || after.type !== 'group') return after;

  const dx = after.x - before.x;
  const dy = after.y - before.y;
  const sx = before.w > 0 ? after.w / before.w : 1;
  const sy = before.h > 0 ? after.h / before.h : 1;

  if (dx === 0 && dy === 0 && sx === 1 && sy === 1) return after;

  const move = (child: Layer): Layer => {
    // Where the child sits inside the box, scaled, then put back against the
    // box's new corner.
    const x = after.x + (child.x - before.x) * sx;
    const y = after.y + (child.y - before.y) * sy;

    const moved = {
      ...child,
      x: round(x),
      y: round(y),
      w: round(child.w * sx),
      h: round(child.h * sy),
    } as Layer;

    if (moved.type === 'text' && (sx !== 1 || sy !== 1)) {
      // One number for two axes: type has a single size, and the smaller of
      // the two keeps the words inside the box they were given.
      const scale = Math.min(sx, sy);

      moved.font = { ...moved.font, size: Math.max(1, Math.round(moved.font.size * scale * 100) / 100) };
    }

    return moved.type === 'group' || moved.type === 'image' ? carry(child as Layer, moved) : moved;
  };

  return { ...after, children: after.children.map(move) };
}

/**
 * A picture's mask, resized with the picture.
 *
 * The mask sits in the picture's own frame, so a picture that grows keeps its
 * frame filling it and a text it is seen through keeps its place - unless the
 * edit changed the mask itself, which then already says what it wants.
 */
function carryMask(before: ImageLayer, after: ImageLayer): ImageLayer {
  const mask = after.mask;

  if (!mask?.layer || mask !== before.mask) return after;

  const sx = before.w > 0 ? after.w / before.w : 1;
  const sy = before.h > 0 ? after.h / before.h : 1;

  if (sx === 1 && sy === 1) return after;

  const inner = mask.layer;
  const scaled = {
    ...inner,
    x: round(inner.x * sx),
    y: round(inner.y * sy),
    w: round(inner.w * sx),
    h: round(inner.h * sy),
  } as typeof inner;

  if (scaled.type === 'text') {
    scaled.font = { ...scaled.font, size: Math.max(1, Math.round(scaled.font.size * Math.min(sx, sy) * 100) / 100) };
  }

  return { ...after, mask: { ...mask, layer: scaled } };
}

const round = (value: number) => Math.round(value * 1000) / 1000;

/** Replaces one layer wherever it sits, including inside a group. */
function withLayer(layers: Layer[], id: string, next: (layer: Layer) => Layer): Layer[] {
  let changed = false;

  const out = layers.map((layer) => {
    if (layer.id === id) {
      changed = true;

      return carry(layer, next(layer));
    }

    if (layer.type === 'group') {
      const children = withLayer(layer.children, id, next);

      if (children !== layer.children) {
        changed = true;

        return { ...layer, children };
      }
    }

    return layer;
  });

  return changed ? out : layers;
}

export const useEditor = create<EditorState>((set, get) => ({
  design: null,
  doc: null,
  pageIndex: 0,
  selection: [],
  view: startingView,
  past: [],
  future: [],
  dirty: false,
  guides: [],
  drafting: null,
  editing: null,
  caret: { start: 0, end: 0 },
  cropping: null,
  cropWas: null,
  shaping: null,
  sampling: null,
  unit: rememberedUnit(),

  open: (design) =>
    set({
      design,
      doc: design.document,
      pageIndex: 0,
      selection: [],
      past: [],
      future: [],
      dirty: false,
      guides: [],
      editing: null,
      caret: { start: 0, end: 0 },
      cropping: null,
      cropWas: null,
      shaping: null,
      sampling: null,
      view: startingView,
    }),

  commit: (label, next, options) => {
    const { doc, past } = get();

    if (!doc) return;

    const after = next(doc);

    // A no-op edit must not fill the history. Dragging an object back to where
    // it started should leave one undo step, not two.
    if (after === doc) return;

    const key = options?.coalesce ?? null;
    const last = past[past.length - 1];
    const merge = key !== null && last?.key === key && Date.now() - last.at < COALESCE_MS;

    const entry: HistoryEntry = { doc, label, key, at: Date.now() };
    const history = merge ? past : past.concat(entry).slice(-HISTORY_LIMIT);

    if (merge && last) last.at = Date.now();

    set({ doc: after, past: history, future: [], dirty: true });
  },

  undo: () => {
    const { past, future, doc, pageIndex, shaping } = get();
    const previous = past[past.length - 1];

    if (!previous || !doc) return;

    set({
      doc: previous.doc,
      past: past.slice(0, -1),
      future: future.concat({ doc, label: previous.label, key: null, at: Date.now() }),
      dirty: true,
      shaping: stillShaping(previous.doc, pageIndex, shaping),
    });
  },

  redo: () => {
    const { past, future, doc, pageIndex, shaping } = get();
    const ahead = future[future.length - 1];

    if (!ahead || !doc) return;

    set({
      doc: ahead.doc,
      past: past.concat({ doc, label: ahead.label, key: null, at: Date.now() }),
      future: future.slice(0, -1),
      dirty: true,
      shaping: stillShaping(ahead.doc, pageIndex, shaping),
    });
  },

  markSaved: () => set({ dirty: false }),

  page: () => {
    const { doc, pageIndex } = get();

    return doc?.pages[pageIndex] ?? null;
  },

  updatePage: (label, next, options) => {
    const index = get().pageIndex;

    get().commit(label, (doc) => withPage(doc, index, next), options);
  },

  updateLayer: (id, label, next, options) => {
    const index = get().pageIndex;

    get().commit(
      label,
      (doc) =>
        withPage(doc, index, (page) => {
          const layers = withLayer(page.layers, id, next);

          return layers === page.layers ? page : { ...page, layers };
        }),
      options
    );
  },

  select: (ids) =>
    set((state) => ({
      selection: ids,
      // Selecting something else ends the edit. Leaving a caret blinking in a
      // box nobody has selected is how you get typing into the wrong layer.
      editing: state.editing && ids.includes(state.editing) ? state.editing : null,
      // Same for a crop: the handles belong to one picture, and leaving them on
      // screen over a different selection is worse than useless.
      cropping: state.cropping && ids.includes(state.cropping) ? state.cropping : null,
      cropWas: state.cropping && ids.includes(state.cropping) ? state.cropWas : null,
      // And a drawing's points: clicking away from the drawing is how a
      // customer says they have finished with them.
      shaping: state.shaping && ids.includes(state.shaping) ? state.shaping : null,
    })),

  toggleSelect: (id) =>
    set((state) => ({
      selection: state.selection.includes(id)
        ? state.selection.filter((other) => other !== id)
        : state.selection.concat(id),
    })),

  selected: () => {
    const page = get().page();

    if (!page) return [];

    return get()
      .selection.map((id) => findLayer(page.layers, id))
      .filter((layer): layer is Layer => layer !== null);
  },

  goToPage: (index) =>
    set((state) => {
      const pages = state.doc?.pages.length ?? 0;

      if (index < 0 || index >= pages || !state.doc) return state;
      if (index === state.pageIndex) return state;

      /*
       * The pages are stacked on screen (canvas/stack.ts) and the view's origin
       * is the active page's corner. Moving the origin by the distance between
       * the two pages keeps everything exactly where it was on screen: the page
       * clicked on becomes the active one without jumping.
       */
      const shift = fromActive(state.doc, index, state.pageIndex, state.view.zoom);

      // Selection is per page; carrying it across would leave the properties
      // panel describing something not on screen.
      return {
        pageIndex: index,
        selection: [],
        editing: null,
        cropping: null,
        cropWas: null,
        shaping: null,
        view: { ...state.view, x: state.view.x + shift.x, y: state.view.y + shift.y },
      };
    }),

  setView: (view) => set((state) => ({ view: { ...state.view, ...view } })),

  setDrafting: (drafting) => set({ drafting }),

  /*
   * A page's ruler guides.
   *
   * Kept in order and never twice in the same place, so the list a customer
   * sees matches the lines on screen. Each change is its own undo step, except
   * a drag, which coalesces into one like every other drag.
   */
  addGuide: (axis, at) =>
    get().updatePage('Guide', (page) => withGuides(page, axis, (list) => tidy([...list, at]))),

  moveGuide: (axis, index, at) =>
    get().updatePage(
      'Move the guide',
      (page) => withGuides(page, axis, (list) => tidy(list.map((was, i) => (i === index ? at : was)))),
      { coalesce: `guide:${axis}:${index}` }
    ),

  removeGuide: (axis, index) =>
    get().updatePage('Remove the guide', (page) => withGuides(page, axis, (list) => list.filter((_, i) => i !== index))),

  clearGuides: () =>
    get().updatePage('Clear the guides', (page) => (page.guides ? { ...page, guides: { x: [], y: [] } } : page)),

  setGuides: (guides) =>
    set((state) => {
      // Guides change on every pointer move during a drag. Bailing out when
      // nothing actually changed keeps that from re-rendering the canvas
      // sixty times a second for no reason.
      if (state.guides.length === 0 && guides.length === 0) return state;

      return { guides };
    }),

  beginEditing: (id, caret) =>
    set((state) => ({
      editing: id,
      selection: state.selection.includes(id) ? state.selection : [id],
      caret: caret ?? { start: 0, end: 0 },
      shaping: null,
    })),

  endEditing: () => set({ editing: null }),

  setCaret: (caret) =>
    set((state) =>
      state.caret.start === caret.start && state.caret.end === caret.end ? state : { caret }
    ),

  beginCrop: (id, was) =>
    set((state) => ({
      cropping: id,
      cropWas: was,
      selection: state.selection.includes(id) ? state.selection : [id],
      // Cropping and typing both want the keyboard.
      editing: null,
      shaping: null,
    })),

  endCrop: (keep = true) =>
    set((state) => {
      if (!state.cropping) return state;

      if (!keep && state.cropWas) {
        const id = state.cropping;
        const was = state.cropWas;

        // Put it back through the normal path, so the restore is itself a step
        // rather than a silent rewrite of the document behind history's back.
        get().updateLayer(id, 'Crop', (layer) =>
          layer.type === 'image' ? { ...layer, crop: was } : layer
        );
      }

      return { cropping: null, cropWas: null };
    }),

  wantPanel: null,

  askPanel: (which) => set({ wantPanel: which }),

  panelAsked: () => set({ wantPanel: null }),

  renaming: null,

  beginRenaming: (id) => set({ renaming: id, wantPanel: 'layers' }),

  endRenaming: () => set({ renaming: null }),

  beginShaping: (id) =>
    set({
      shaping: id,
      // Only one drawing's points at a time, so only that drawing is selected.
      selection: [id],
      editing: null,
      cropping: null,
      cropWas: null,
    }),

  endShaping: () => set({ shaping: null }),

  // Plain setters, wrapped so the function is stored rather than called:
  // zustand's set() treats a function argument as an updater.
  beginSampling: (to) => set({ sampling: to }),
  endSampling: () => set({ sampling: null }),

  setUnit: (unit) => {
    try {
      window.localStorage.setItem(UNIT_KEY, unit);
    } catch {
      // Not remembered, but still used for as long as the page is open.
    }

    set({ unit });
  },
}));

/** Whether there is anything to undo or redo, for the top bar. */
export const useCanUndo = () => useEditor((s) => s.past.length > 0);
export const useCanRedo = () => useEditor((s) => s.future.length > 0);

/**
 * The selected layers, for components.
 *
 * NOT `useEditor((s) => s.selected())`. A selector that builds a new array on
 * every read never compares equal to the last one, so the component re-renders,
 * which reads again, forever - React gives up at fifty and throws. The store
 * method stays, for callers outside React that read it once; components take
 * this, which derives from two stable references and memoises.
 */
export function useSelectedLayers(): Layer[] {
  const selection = useEditor((s) => s.selection);
  const page = useEditor((s) => s.doc?.pages[s.pageIndex] ?? null);

  return useMemo(() => {
    if (!page) return [];

    return selection
      .map((id) => findLayer(page.layers, id))
      .filter((layer): layer is Layer => layer !== null);
  }, [selection, page]);
}
